import React, { useCallback } from 'react';
import cn from 'classnames';

import { useToggle, useQueryParam, useDidUpdateEffect } from 'Shared/utils/hooks';
import WpBadge from 'Wpds/components/badge/WpBadge';
import { WorkspaceTemplate } from 'Shared/constants/types';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from 'Wpds/components/tooltip';

import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import TemplatePreviewModal from './TemplatePreviewModal/TemplatePreviewModal';

import './WorkspaceTemplateBlock.scss';

type Props = {
  isInitialChoice?: boolean;
  isGroupWorkspace?: boolean;
  template: WorkspaceTemplate;
  startWithPreview?: boolean;
  onSubmit?: Function;
  onPreviewTemplateBack?: Function;
  ovPreviewTemplateClose?: Function;
};

function WorkspaceTemplateBlock({
  template,
  isInitialChoice = false,
  startWithPreview = false,
  onSubmit,
  ovPreviewTemplateClose,
  onPreviewTemplateBack,
  isGroupWorkspace = false,
}: Props) {
  const [isPreviewModalOpen, toggleIsPreviewModalOpen] = useToggle(startWithPreview);
  const [, setTemplatesModalParam] = useQueryParam(QueryParamConstants.TEMPLATES_MODAL);

  useDidUpdateEffect(() => {
    if (isInitialChoice) setTemplatesModalParam(isPreviewModalOpen ? 'choose_templates_page' : null);

    return () => {
      if (isInitialChoice) setTemplatesModalParam(null);
    };
  }, [isPreviewModalOpen]);

  const handleBackClick = useCallback(() => {
    toggleIsPreviewModalOpen();
    if (onPreviewTemplateBack) onPreviewTemplateBack();
  }, [onPreviewTemplateBack]);

  const handlePreviewModalClose = useCallback(() => {
    toggleIsPreviewModalOpen();
    if (ovPreviewTemplateClose) ovPreviewTemplateClose();
  }, [ovPreviewTemplateClose]);

  return (
    <>
      {isPreviewModalOpen && (
        <TemplatePreviewModal
          isInitialChoice={isInitialChoice}
          startWithPreview={startWithPreview}
          templateSlug={template.slug}
          onToggle={handlePreviewModalClose}
          onBackClick={handleBackClick}
          onSubmit={onSubmit}
        />
      )}
      <WpContainer
        className={cn('WorkspaceTemplateBlock position-relative m-1', {
          'WorkspaceTemplateBlock--initial-choice': isInitialChoice,
        })}
        onClick={toggleIsPreviewModalOpen}
      >
        <WpContainerBody padding={'05'} className="d-flex flex-column h-100">
          {!isGroupWorkspace && (template.recommended || template.piRecommended) && (
            <div className="align-self-end">
              <WpBadge size="sm" color="blue">
                {template.recommended ? 'Recommended' : 'PI Recommended'}
              </WpBadge>
            </div>
          )}
          {!template.recommended && template.isCustom && (
            <WpTooltip>
              <WpTooltipTrigger>
                <div className="align-self-end">
                  <WpBadge size="sm">{template.isPublic ? 'Public' : 'Private'}</WpBadge>
                </div>
              </WpTooltipTrigger>
              <WpTooltipContent>
                {template.isPublic
                  ? 'Anyone at your company can see and use this template'
                  : 'Only you can see this template'}
              </WpTooltipContent>
            </WpTooltip>
          )}
          <div className="WorkspaceTemplateBlock__title my-2">{template.name}</div>
          <div className="WorkspaceTemplateBlock__description mb-3">{template.description}</div>
          <div className="WorkspaceTemplateBlock__preview align-self-end mt-auto">Preview</div>
        </WpContainerBody>
      </WpContainer>
    </>
  );
}

export default WorkspaceTemplateBlock;
